import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export type Pestana = {
  readonly id: string;
  readonly titulo: string;
  /** Texto corto junto al título (recuento, estado). */
  readonly insignia?: string | undefined;
  readonly contenido: ReactNode;
};

export type PestanasProps = {
  readonly pestanas: readonly Pestana[];
  /** Pestaña inicial si la URL no indica ninguna. */
  readonly inicial: string;
  readonly etiqueta: string;
};

/**
 * Pestañas accesibles (tablist/tab/tabpanel, flechas y Home/End). Todos los
 * paneles permanecen montados y solo se oculta el inactivo: los que siguen
 * trabajos por polling no deben pararse al cambiar de pestaña. La pestaña
 * activa va en el hash de la URL para que recargar vuelva al mismo sitio.
 */
export function Pestanas({ pestanas, inicial, etiqueta }: PestanasProps): React.JSX.Element {
  const base = useId();
  const desdeHash = (): string | null => {
    const hash = window.location.hash.replace(/^#/, '');
    return pestanas.some((p) => p.id === hash) ? hash : null;
  };
  const [activa, setActiva] = useState<string>(() => desdeHash() ?? inicial);
  const botones = useRef<Map<string, HTMLButtonElement>>(new Map());

  const ids = pestanas.map((p) => p.id).join(',');
  useEffect(() => {
    const alCambiar = (): void => {
      const hash = window.location.hash.replace(/^#/, '');
      if (ids.split(',').includes(hash)) setActiva(hash);
    };
    window.addEventListener('hashchange', alCambiar);
    return () => window.removeEventListener('hashchange', alCambiar);
  }, [ids]);

  function elegir(id: string): void {
    setActiva(id);
    // replaceState: cambiar de pestaña no debe llenar el historial.
    window.history.replaceState(null, '', `#${id}`);
  }

  function teclado(evento: KeyboardEvent<HTMLButtonElement>): void {
    const indice = pestanas.findIndex((p) => p.id === activa);
    const ir = (i: number): void => {
      const destino = pestanas[(i + pestanas.length) % pestanas.length]!;
      elegir(destino.id);
      botones.current.get(destino.id)?.focus();
    };
    if (evento.key === 'ArrowRight') { evento.preventDefault(); ir(indice + 1); }
    else if (evento.key === 'ArrowLeft') { evento.preventDefault(); ir(indice - 1); }
    else if (evento.key === 'Home') { evento.preventDefault(); ir(0); }
    else if (evento.key === 'End') { evento.preventDefault(); ir(pestanas.length - 1); }
  }

  return (
    <div>
      <div role="tablist" aria-label={etiqueta} className="pestanas">
        {pestanas.map((p) => {
          const seleccionada = p.id === activa;
          return (
            <button
              key={p.id}
              ref={(el) => { if (el) botones.current.set(p.id, el); else botones.current.delete(p.id); }}
              type="button"
              role="tab"
              id={`${base}-tab-${p.id}`}
              aria-selected={seleccionada}
              aria-controls={`${base}-panel-${p.id}`}
              tabIndex={seleccionada ? 0 : -1}
              className={seleccionada ? 'pestana activa' : 'pestana'}
              onClick={() => elegir(p.id)}
              onKeyDown={teclado}
            >
              {p.titulo}
              {p.insignia !== undefined && <span className="insignia">{p.insignia}</span>}
            </button>
          );
        })}
      </div>
      {pestanas.map((p) => (
        <div
          key={p.id}
          role="tabpanel"
          id={`${base}-panel-${p.id}`}
          aria-labelledby={`${base}-tab-${p.id}`}
          hidden={p.id !== activa}
        >
          {p.contenido}
        </div>
      ))}
    </div>
  );
}
